'use client';

import React, { useState, useMemo, useRef } from 'react';
import { BgDpcCodeType } from '@/data/dtos/Resouces/BgDentalProcedureCodes/BgDpcCodeType';
import { BgDpcCodeCategoryType } from '@/data/dtos/Resouces/BgDentalProcedureCodes/BgDpcCodeCategoryType';
import { useTranslations } from 'next-intl';
import DentalCodesCatalogFiltration from '@/app-components/resouces/bg-dpc/DentalCodesCatalogFiltration';
import { BgDpcRecipeType } from "@/data/dtos/Resouces/BgDentalProcedureCodes/BgDpcRecipeType";
import ItemDentalCodeInteractive from "@/app-components/resouces/bg-dpc/ItemDentalCodeInteractive";
import ResourceMarkdown from '@/app-components/resouces/ResourceMarkdown';

interface SearchFormDentalCodesProps {
	codes: BgDpcCodeType[];
	codeCategories: BgDpcCodeCategoryType[];
	treatments: BgDpcRecipeType[];
}

export default function SearchFormDentalCodes({ codes, codeCategories, treatments }: SearchFormDentalCodesProps) {
	const MAX_ITEMS = 5;
	const [selectedCategory, setSelectedCategory] = useState('all');
	const [searchQuery, setSearchQuery] = useState(''); // State for search query
	const [showAllItems, setShowAllItems] = useState(false); // State to toggle all items visibility
	const t = useTranslations('page.r-bg-dental-procedure-codes');
	const codesRef = useRef<HTMLDivElement>(null); // Ref for the codes container

	// Compute filtered codes based on selectedCategory and searchQuery
	const filteredCodes = useMemo(() => {
		// Filter by category
		let filtered = selectedCategory === 'all' ? codes : codes.filter((codeItem) => codeItem.category.code === Number(selectedCategory));

		// Further filter by search query
		if (searchQuery.trim()) {
			const lowerQuery = searchQuery.toLowerCase();
			filtered = filtered.filter(
				(codeItem) =>
					codeItem.name.toLowerCase().includes(lowerQuery) ||
					codeItem.description.toLowerCase().includes(lowerQuery) ||
					codeItem.code.toLowerCase().includes(lowerQuery)
			);
		}

		return filtered;
	}, [selectedCategory, searchQuery, codes]);

	// Limit the displayed codes based on the `showAllItems` flag
	const displayedCodes = showAllItems ? filteredCodes : filteredCodes.slice(0, MAX_ITEMS);

	const handleToggle = () => {
		setShowAllItems(!showAllItems);

		// Scroll to the codes container, only when showing less.
		if (showAllItems && codesRef.current) {
			codesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	};

	return (
		<section className="pt-0 pb-0">
			<div className="container position-relative z-index-1 mb-5 mb-lg-7 pb-sm-6">
				<div className="row">
					<div className="col-lg-10 col-xl-9 text-center mx-auto">
						<h2 className="mb-4 h2">{t('dental_code_section.title')}</h2>

						<div className="bg-light border rounded-3 position-relative px-4 py-3 mt-5">
							<form className="row g-3 justify-content-center align-items-center">
								<div className="col-lg-6">
									<div className="position-relative">
										{/* Search Input */}
										<input
											className="form-control form-control-lg me-1 ps-3"
											type="text"
											placeholder={t('dental_code_section.search_placeholder')}
											value={searchQuery}
											onChange={(e) => setSearchQuery(e.target.value)} // Update searchQuery
										/>
									</div>
								</div>

								<div className="col-lg-6">
									<div className="position-relative">
										{/* Category Filter */}
										<DentalCodesCatalogFiltration
											codeCategories={codeCategories}
											onCategoryChange={setSelectedCategory}
											className="form-select form-select-lg ps-3"
										/>
									</div>
								</div>
							</form>
						</div>

						<div className="small mt-3">
							<ResourceMarkdown>
								{t('dental_code_section.popular_searches')}
							</ResourceMarkdown>
						</div>
					</div>
				</div>

				<div className="row g-2 g-xl-4 pt-3" id="codes" ref={codesRef}>
					{displayedCodes.length > 0 ? (
						displayedCodes.map((codeItem) => (
							<div className="col-12" key={codeItem.code}>
								<ItemDentalCodeInteractive
									code={codeItem}
									treatments={treatments}
									searchQuery={searchQuery}
								/>
							</div>
						))
					) : (
						<div className="col-12 text-center muted">
							<ResourceMarkdown>
								{t('common_used.no-results', { search: searchQuery })}
							</ResourceMarkdown>
						</div>
					)}
				</div>

				{/* Toggle Link */}
				{filteredCodes.length > MAX_ITEMS && (
					<div className="text-center mt-4">
						<button
							className="btn btn-link heading-color text-decoration-underline"
							onClick={handleToggle}
						>
							{showAllItems ? (
								<>
									{t('common_used.show_fewer_results')}
									<i className="bi bi-chevron-up ms-1"></i>
								</>
							) : (
								<>
									{t('common_used.see_all', { results_number: filteredCodes.length - MAX_ITEMS })}
									<i className="bi bi-chevron-down ms-1"></i>
								</>
							)}
						</button>
					</div>
				)}
			</div>
		</section>
	);
}
